import { Box, Button, Card, Table, TableBody, TableCell, TableContainer, TablePagination, TableRow, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import api from '../../../service/api'
import TableHeader from '../../common/TableHeader'
import { formatDate } from '../../../utils/utilities'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import noAssignmentImg from '../../../assets/noassignment.svg'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import AuditQuestionBankViewer from './AuditQuestionBankViewer'

const columns =
    [
        { id: 2, name: "Questions Bank", minWidth: "120px", padding: "10px 20px 10px 40px" },
        { id: 3, name: 'Faculty name', minWidth: "150px", align: "left", padding: "10px 20px" },
        { id: 6, name: 'DEPARTMENT', padding: "10px 20px" },
        { id: 4, name: "Subject", padding: "10px 20px" },
        { id: 5, align: "center", name: "Created on", minWidth: "150px", padding: "10px 20px" },
        { id: 7, align: "center", name: "questions", padding: "10px 40px 10px 20px" },
    ]


const AuditQuestionBanksList = () => {


    const [searchParams, setSearchParams] = useSearchParams( { searchText: "", month: "", selectedDepartment: "all", selectedInstitute: "all", view: "tabular", sortByName: "1", sortBySubmissionsCount: "1", sortByAssignmentCount: "1" } )
    const searchText = searchParams.get( 'searchText' ) || ""
    const selectedInstitute = searchParams.get( 'selectedInstitute' ) || "all"
    const selectedDepartment = searchParams.get( 'selectedDepartment' ) || "all"

    const [page, setPage] = useState( 0 )
    const [error, setError] = useState( null )
    const [questionBanks, setQuestionBanks] = useState( [] )
    const [loading, setLoading] = useState( true )
    const [selectedQuestionBank, setSelectedQuestionBank] = useState( null )
    const [rowsPerPage, setRowsPerPage] = useState( +( localStorage.getItem( 'adminAuditRowsPerPage' ) || 50 ) )

    const { table, palette, border } = useTheme()

    const navigate = useNavigate()


    const filterFunction = item => {
        const searchValue = new RegExp( searchText?.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' ), 'i' )
        if ( selectedDepartment !== "all" ) {
            const [department_id, legacy_department_id] = selectedDepartment?.split( "-" )
            const doesMatchDepartment = ( item.legacy_system_data && +legacy_department_id === +item?.creator_data?.department_id ) || ( !item.legacy_system_data && +department_id === +item?.creator_data?.department_id )
            return doesMatchDepartment && ( item?.creator_data?.department_name?.match( searchValue ) || item.faculty_name?.match( searchValue ) || item.created_by?.match( searchValue ) )
        } else {
            return item?.creator_data?.department_name?.match( searchValue ) || item.faculty_name?.match( searchValue ) || item.created_by?.match( searchValue )
        }
    }

    const removeAppliedFilters = () => {
        setSearchParams( prev => {
            prev.set( 'searchText', '' )
            prev.set( 'selectedInstitute', "all" )
            prev.set( 'selectedDepartment', "all" )
            return prev
        } )
    }

    const handleDialogClose = () => {
        setSelectedQuestionBank( null )
    }


    const getData = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data } } = await api.audit.getQuestionBanksForAuditing( { institute: selectedInstitute !== "all" ? selectedInstitute : "" } )
            setQuestionBanks( data )
        } catch ( err ) {
            console.log( err )
            setError( err?.message?.data?.message || "Something went wrong while fetching questionBanks." )
        } finally {
            setLoading( false )
        }
    }, [selectedInstitute] )

    useEffect( () => {
        getData()
    }, [getData] )


    return (
        <Card sx={{ flexShrink: 0, display: "flex", flexDirection: "column" }}>

            {selectedQuestionBank && <AuditQuestionBankViewer questionbank={selectedQuestionBank} handleClose={handleDialogClose} openState={Boolean( selectedQuestionBank )} />}

            <Box padding="20px" display="flex" gap="20px" justifyContent="space-between" >
                <Typography color="primaryDark.main" fontSize="16px" variant='h6'>Question banks</Typography>
                <Button size="small" onClick={() => navigate( "/admin/questionBanks" )} variant="contained" disableElevation sx={{ height: "33.5px", textTransform: "capitalize", background: "#FFE0E0", border: `1px solid #FF6C8F`, color: "#FF736F", '&:hover': { background: "#FFE0E0", color: "#FF736F" } }} >View Stats</Button>
            </Box >

            {!loading && questionBanks.filter( filterFunction ).length > 0 && <Box paddingBottom="20px" overflow="hidden" border={border[1]} borderRadius="10px" bgcolor={palette.contentBg} margin="0 20px">
                <TableContainer sx={{ overflow: "auto", maxHeight: "500px" }} className='custom-scrollbar' >
                    <Table stickyHeader>
                        <TableHeader columns={columns} />
                        <TableBody>
                            {questionBanks.filter( filterFunction ).slice( page * rowsPerPage, page * rowsPerPage + rowsPerPage ).map( ( row, index ) => {
                                return <TableRow onClick={() => setSelectedQuestionBank( row )} className='table-row' key={row.id} sx={{ cursor: "pointer", ":hover": { bgcolor: palette.rowHover } }} >
                                    <TableCell sx={{ padding: columns[0].padding || "10px 20px", color: table.color, textTransform: "capitalize", fontSize: "12px" }}><strong>{row.name}</strong></TableCell>
                                    <TableCell align={columns[1].align || "left"} sx={{ fontSize: "12px", padding: columns[1].padding || "10px 20px", textTransform: "capitalize", color: table.color }}><Typography maxWidth="100px" fontSize="12px" ><strong>{row.faculty_name?.toLowerCase()}</strong></Typography></TableCell>
                                    <TableCell align={columns[2].align || "left"} sx={{ fontSize: "12px", padding: columns[2].padding || "10px 20px", textTransform: "capitalize", color: table.color, }}><strong>{row?.creator_data?.department_name?.toLowerCase()}</strong></TableCell>
                                    <TableCell align={columns[3].align || "left"} sx={{ fontSize: "12px", padding: columns[3].padding || "10px 20px", color: table.color }}><strong>{row.subject?.subject_name || "Not available"}</strong></TableCell>
                                    <TableCell align={columns[4].align || "left"} sx={{ fontSize: "12px", padding: columns[4].padding || "10px 20px", color: table.color }}><strong>{formatDate( row.created_at )}</strong></TableCell>
                                    <TableCell align={columns[5].align || "left"} sx={{ fontSize: "12px", padding: columns[5].padding || "10px 20px", color: table.color }}><strong>{row.questions}</strong></TableCell>
                                </TableRow>
                            } )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    sx={{ background: table.headerBg, position: "sticky", bottom: 0, flexShrink: 0 }}
                    rowsPerPageOptions={[50, 100, 150, 200]}
                    component="div"
                    count={questionBanks.filter( filterFunction ).length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={( event, val ) => setPage( val )}
                    onRowsPerPageChange={( event ) => {
                        setRowsPerPage( parseInt( event.target.value, 10 ) )
                        localStorage.setItem( 'adminAuditRowsPerPage', event.target.value )
                        setPage( 0 )
                    }}
                />
            </Box>}
            {!loading && questionBanks.filter( filterFunction ).length === 0 && <Box flexDirection="column" padding="20px" display="flex" alignItems="center" justifyContent="center" gap="20px">
                <img src={noAssignmentImg} height="120px" alt="No assignmets to show" />
                <Typography textAlign="center" variant='subtitle2'>No questionBanks were found for applied filters!</Typography>
                {questionBanks.length > 0 && <Button onClick={removeAppliedFilters} sx={{ textTransform: "capitalize" }} color="secondary" variant="contained" disableElevation>Remove filters</Button>}
            </Box>}
            {error && <TryAgainMessageBlock getData={getData} err={error} loading={loading} />}
            {loading && <DataLoadingSpinner waitingMessage="Loading question banks please wait...." />}
        </Card>
    )
}

export default AuditQuestionBanksList
